"use client";

import { useTranslations } from "next-intl";
import { createLegalRichTags, legalRichValues } from "@/lib/legal/page-rich-tags";
import { ROUTE_GUTTER_CLASS } from "@/lib/route-layout";
import { cn } from "@/lib/utils";

const S1_KEYS = ["recording", "offPlatform", "respectful"] as const;
const S2_KEYS = ["bullying", "hateSpeech", "sexualHarassment", "harmfulContent", "minors"] as const;
const S3_KEYS = ["externalPayment", "redirect", "negotiateOffPlatform", "priceRedirect"] as const;
const S4_KEYS = ["truthful", "originalPhotos", "copyright", "misrepresentation"] as const;
const S4_PROHIBITED_KEYS = ["adult", "counterfeit", "regulated", "preorders"] as const;
const S5_KEYS = ["illegalConduct", "circumventProtections", "circumventFees", "fraud", "spam", "multipleAccounts"] as const;

export default function CommunityGuidelines() {
  const t = useTranslations("legal.communityGuidelines");
  const richTags = createLegalRichTags();
  const richValues = legalRichValues();

  return (
    <div className={cn("pt-4 pb-6 flex flex-col justify-center items-center min-h-screen text-left bg-background py-6", ROUTE_GUTTER_CLASS)}>
      <h1 className="pt-6 text-5xl font-bold text-foreground mb-4">{t("title")}</h1>
      <p className="text-lg text-muted-foreground mb-6">{t("effectiveDate")}</p>

      <div className="w-full max-w-4xl text-foreground pb-8">
        <p className="mb-4">{t.rich("intro", richTags)}</p>
        <p className="mb-4">{t("acceptance")}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s1.title")}</h2>
        <p className="mb-4">{t("s1.intro")}</p>
        <ul className="list-disc pl-6 mb-4">
          {S1_KEYS.map((key) => (
            <li key={key}>{t.rich(`s1.bullets.${key}`, richTags)}</li>
          ))}
        </ul>

        <h2 className="text-2xl font-semibold mb-4">{t("s2.title")}</h2>
        <p className="mb-4">{t("s2.intro")}</p>
        <ul className="list-disc pl-6 mb-4">
          {S2_KEYS.map((key) => (
            <li key={key}>{t(`s2.bullets.${key}`)}</li>
          ))}
        </ul>

        <h2 className="text-2xl font-semibold mb-4">{t("s3.title")}</h2>
        <p className="mb-4">{t("s3.checkoutRequired")}</p>
        <p className="mb-4">{t("s3.includesIntro")}</p>
        <ul className="list-disc pl-6 mb-4">
          {S3_KEYS.map((key) => (
            <li key={key}>{t(`s3.bullets.${key}`)}</li>
          ))}
        </ul>
        <p className="mb-4">{t.rich("s3.termsReference", richTags)}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s4.title")}</h2>
        <p className="mb-4">{t("s4.intro")}</p>
        <ul className="list-disc pl-6 mb-4">
          {S4_KEYS.map((key) => (
            <li key={key}>{t(`s4.bullets.${key}`)}</li>
          ))}
        </ul>

        <h3 className="text-xl font-medium mb-2">{t("s4.prohibitedItemsTitle")}</h3>
        <ul className="list-disc pl-6 mb-4">
          {S4_PROHIBITED_KEYS.map((key) => (
            <li key={key}>{t(`s4.prohibitedItems.${key}`)}</li>
          ))}
        </ul>

        <h2 className="text-2xl font-semibold mb-4">{t("s5.title")}</h2>
        <ul className="list-disc pl-6 mb-4">
          {S5_KEYS.map((key) => (
            <li key={key}>{t(`s5.bullets.${key}`)}</li>
          ))}
        </ul>

        <h2 className="text-2xl font-semibold mb-4">{t("s6.title")}</h2>
        <p className="mb-4">{t("s6.monitoring")}</p>
        <p className="mb-4">{t("s6.enforcement")}</p>
        <p className="mb-4">{t.rich("s6.privacyReference", richTags)}</p>

        <h2 className="text-2xl font-semibold mb-4">{t("s7.title")}</h2>
        <p className="mb-4">{t.rich("s7.report", { ...richTags, ...richValues })}</p>
        <p className="mb-4">{t.rich("s7.termsReference", richTags)}</p>
      </div>
    </div>
  );
}
